import {
  ArrowsClockwiseIcon,
  BellRingingIcon,
  BookOpenTextIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  CalendarBlankIcon,
  CalendarCheckIcon,
  ChartBarIcon,
  ChartLineUpIcon,
  ChartPieSliceIcon,
  ChatsCircleIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  EnvelopeSimpleIcon,
  FilePdfIcon,
  FilesIcon,
  FileTextIcon,
  FunnelIcon,
  GearSixIcon,
  GlobeSimpleIcon,
  HandshakeIcon,
  LayoutIcon,
  LightningIcon,
  LinkSimpleIcon,
  ListChecksIcon,
  LockSimpleIcon,
  MegaphoneIcon,
  PackageIcon,
  PaperPlaneTiltIcon,
  PathIcon,
  PhoneIcon,
  PuzzlePieceIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  SignatureIcon,
  SparkleIcon,
  StackIcon,
  StarIcon,
  TagIcon,
  TargetIcon,
  TicketIcon,
  TimerIcon,
  TrendUpIcon,
  TruckIcon,
  UserCircleIcon,
  UsersThreeIcon,
  VideoCameraIcon,
} from '@phosphor-icons/react'

import type { ModuleLandingConfig } from './ModuleUpgradeLanding'

interface SupplierLandingTerminology {
  singular: string
  plural: string
  Singular: string
  Plural: string
}

interface ModuleLandingConfigOptions {
  supplierTerminology?: SupplierLandingTerminology
}

const DEFAULT_SUPPLIER_LANDING_TERMINOLOGY: SupplierLandingTerminology = {
  singular: 'vendor',
  plural: 'vendors',
  Singular: 'Vendor',
  Plural: 'Vendors',
}

/*
 * The previews tell one story: a small creative agency and its clients
 * (Harbor & Pine Coffee, Fieldstone Dental, Northline Outfitters, Bluewater
 * Credit Union, Maple Street Bakery, Lumen Architects). Every claim and every
 * status in them is something the module really does or really shows — the
 * default stage, status, and priority names, in the app's sentence case.
 * Copy addressed to the reader stays neutral ("your team", "your website"):
 * not every workspace is an agency.
 */

const forms: ModuleLandingConfig = {
  slug: 'forms',
  title: 'Forms',
  subtitle:
    'Inquiries, intake questionnaires, and requests that file themselves — each submission lands as a contact, ticket, deal, or collection entry your team can act on.',
  icon: ClipboardTextIcon,
  accent: 'sky',
  features: [
    {
      icon: LayoutIcon,
      title: 'Drag-and-drop builder',
      body: 'Sections, pages, and show-or-hide rules, so a kickoff questionnaire only asks what applies to that client.',
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Submissions become records',
      body: 'Send each response to Contacts, Tickets, Deals, Tasks, Events, or a collection, with fields mapped once.',
    },
    {
      icon: PaperPlaneTiltIcon,
      title: 'Share a link or embed it',
      body: 'Publish a public link, or embed the form on the sites you allow, like your contact page.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Spam protection',
      body: 'Turn on CAPTCHA for public forms, or password-protect one meant for a single client.',
    },
  ],
  preview: {
    label: 'forms',
    caption:
      'Each form names where its submissions go, so the new-client inquiry fills your contact list and the support form opens tickets.',
    columns: [
      { key: 'name', label: 'Form' },
      { key: 'target', label: 'Creates' },
      { key: 'submissions', label: 'Submissions', align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'NC', tone: 'sky' },
        cells: {
          name: 'New client inquiry',
          target: 'Contacts',
          submissions: '142',
          status: { kind: 'pill', label: 'Published', tone: 'mint' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'WS', tone: 'rose' },
        cells: {
          name: 'Website support request',
          target: 'Tickets',
          submissions: '89',
          status: { kind: 'pill', label: 'Published', tone: 'mint' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PK', tone: 'lilac' },
        cells: {
          name: 'Project kickoff questionnaire',
          target: 'Deals',
          submissions: '34',
          status: { kind: 'pill', label: 'Published', tone: 'mint' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'WB', tone: 'amber' },
        cells: {
          name: 'Workshop booking request',
          target: 'Events',
          submissions: '56',
          status: { kind: 'pill', label: 'Closed', tone: 'slate' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'PR', tone: 'mint' },
        cells: {
          name: 'Photo release',
          target: 'Photo releases',
          submissions: '211',
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
        },
      },
    ],
  },
}

const contacts: ModuleLandingConfig = {
  slug: 'contacts',
  title: 'Contacts',
  subtitle:
    'Everyone you work with — prospects, client stakeholders, referral partners — with where they came from and where they stand.',
  icon: FunnelIcon,
  accent: 'orange',
  features: [
    {
      icon: TargetIcon,
      title: 'Lifecycle stages',
      body: 'New, qualified, customer, churned. Rename the stages to match how your team talks about people.',
    },
    {
      icon: UserCircleIcon,
      title: 'Source and score',
      body: 'See which referral, event, or form brought each person in, and who is worth a call this week.',
    },
    {
      icon: HandshakeIcon,
      title: 'Deals from the record',
      body: 'Start a deal from a contact and it arrives with the contact and company already filled in.',
    },
    {
      icon: ChatsCircleIcon,
      title: 'One shared history',
      body: 'Emails, files, deals, and related people on one record, so a handoff never starts from zero.',
    },
  ],
  preview: {
    label: 'contacts',
    caption:
      'The people behind your clients and prospects, with the stage they are in and the teammate who owns the relationship.',
    columns: [
      { key: 'name', label: 'Contact' },
      { key: 'company', label: 'Company' },
      { key: 'source', label: 'Source' },
      { key: 'stage', label: 'Lifecycle' },
      { key: 'owner', label: 'Owner' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'DW', tone: 'mint' },
        cells: {
          name: 'Dana Whitfield',
          company: 'Harbor & Pine Coffee',
          source: { kind: 'muted', value: 'Referral' },
          stage: { kind: 'pill', label: 'Customer', tone: 'mint' },
          owner: { kind: 'muted', value: 'Maya K.' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MB', tone: 'sky' },
        cells: {
          name: 'Marcus Bell',
          company: 'Fieldstone Dental',
          source: { kind: 'muted', value: 'Website form' },
          stage: { kind: 'pill', label: 'Qualified', tone: 'lilac' },
          owner: { kind: 'muted', value: 'Jordan E.' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'ER', tone: 'rose' },
        cells: {
          name: 'Elena Ruiz',
          company: 'Northline Outfitters',
          source: { kind: 'muted', value: 'Open house' },
          stage: { kind: 'pill', label: 'New', tone: 'sky' },
          owner: { kind: 'muted', value: 'Maya K.' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'TH', tone: 'lilac' },
        cells: {
          name: 'Tom Haddad',
          company: 'Bluewater Credit Union',
          source: { kind: 'muted', value: 'LinkedIn' },
          stage: { kind: 'pill', label: 'Customer', tone: 'mint' },
          owner: { kind: 'muted', value: 'Priya N.' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'GL', tone: 'amber' },
        cells: {
          name: 'Grace Liu',
          company: 'Lumen Architects',
          source: { kind: 'muted', value: 'Referral' },
          stage: { kind: 'pill', label: 'Qualified', tone: 'lilac' },
          owner: { kind: 'muted', value: 'Jordan E.' },
        },
      },
    ],
  },
}

const deals: ModuleLandingConfig = {
  slug: 'deals',
  title: 'Deals',
  subtitle:
    'Every piece of new work, from the first call to the signed contract — with a forecast you can actually plan hiring around.',
  icon: HandshakeIcon,
  accent: 'emerald',
  features: [
    {
      icon: TrendUpIcon,
      title: 'Weighted forecast',
      body: 'Each stage carries a win probability, and any deal can override it. The forecast weighs them for you.',
    },
    {
      icon: ListChecksIcon,
      title: 'Stages that fit your sales',
      body: 'Prospecting to closed won by default. Rename, reorder, or add stages, and set each one’s odds.',
    },
    {
      icon: FileTextIcon,
      title: 'Quotes and contracts',
      body: 'Send a quote from the deal, see when the client accepts it, and turn the win into a contract.',
    },
    {
      icon: SparkleIcon,
      title: 'Ask Benny',
      body: 'Open Benny on any deal and he already knows the client, the amount, and what happened last.',
    },
  ],
  preview: {
    label: 'deals',
    caption:
      'Open work across your clients, with the stage it has reached and when you expect it to close.',
    columns: [
      { key: 'name', label: 'Deal' },
      { key: 'company', label: 'Client' },
      { key: 'value', label: 'Amount', align: 'right' },
      { key: 'stage', label: 'Stage' },
      { key: 'close', label: 'Expected close' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'WR', tone: 'mint' },
        cells: {
          name: 'Website redesign',
          company: 'Harbor & Pine Coffee',
          value: { kind: 'money', value: '$48,000' },
          stage: { kind: 'pill', label: 'Proposal', tone: 'lilac' },
          close: { kind: 'muted', value: 'Nov 14' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BR', tone: 'sky' },
        cells: {
          name: 'Brand refresh',
          company: 'Fieldstone Dental',
          value: { kind: 'money', value: '$32,500' },
          stage: { kind: 'pill', label: 'Negotiation', tone: 'amber' },
          close: { kind: 'muted', value: 'Nov 28' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'MB', tone: 'rose' },
        cells: {
          name: 'Mobile banking launch',
          company: 'Bluewater Credit Union',
          value: { kind: 'money', value: '$124,000' },
          stage: { kind: 'pill', label: 'Contract sent', tone: 'sky' },
          close: { kind: 'muted', value: 'Dec 5' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'SC', tone: 'lilac' },
        cells: {
          name: 'Spring catalog shoot',
          company: 'Northline Outfitters',
          value: { kind: 'money', value: '$18,000' },
          stage: { kind: 'pill', label: 'Discovery', tone: 'slate' },
          close: { kind: 'muted', value: 'Jan 16' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'SR', tone: 'amber' },
        cells: {
          name: 'Social media retainer',
          company: 'Maple Street Bakery',
          value: { kind: 'money', value: '$4,500/mo' },
          stage: { kind: 'pill', label: 'Closed won', tone: 'mint' },
          close: { kind: 'muted', value: 'Oct 2' },
        },
      },
    ],
  },
}

const team: ModuleLandingConfig = {
  slug: 'team',
  title: 'Team',
  subtitle:
    'Who is booked, who has room, and what each hour costs and earns — so you can say yes to new work with confidence.',
  icon: UsersThreeIcon,
  accent: 'violet',
  features: [
    {
      icon: ChartLineUpIcon,
      title: 'Utilization at a glance',
      body: 'See who is overbooked, who is light, and how next month’s capacity compares to what’s sold.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Rates and rate cards',
      body: 'Bill and cost rates per person, with client or project overrides, so margins roll up on their own.',
    },
    {
      icon: CalendarBlankIcon,
      title: 'Schedules and time off',
      body: 'Working hours, holidays, and time-off requests with approval, all counted against capacity.',
    },
    {
      icon: TargetIcon,
      title: 'Find the right person',
      body: 'Search by skill and availability to staff the next project without a round of messages.',
    },
  ],
  preview: {
    label: 'team · this week',
    caption:
      'How booked each person is this week, what they are working on, and the rate their time bills at.',
    columns: [
      { key: 'name', label: 'Member' },
      { key: 'role', label: 'Role' },
      { key: 'utilization', label: 'Booked', align: 'right' },
      { key: 'project', label: 'Main project' },
      { key: 'rate', label: 'Bill rate', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'MK', tone: 'lilac' },
        cells: {
          name: 'Maya Kim',
          role: 'Creative director',
          utilization: { kind: 'pill', label: '104%', tone: 'rose' },
          project: 'Harbor & Pine website',
          rate: { kind: 'money', value: '$185/hr' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'JE', tone: 'sky' },
        cells: {
          name: 'Jordan Ellis',
          role: 'Senior designer',
          utilization: { kind: 'pill', label: '86%', tone: 'mint' },
          project: 'Fieldstone brand refresh',
          rate: { kind: 'money', value: '$145/hr' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PN', tone: 'rose' },
        cells: {
          name: 'Priya Nair',
          role: 'Project manager',
          utilization: { kind: 'pill', label: '78%', tone: 'mint' },
          project: 'Bluewater mobile app',
          rate: { kind: 'money', value: '$125/hr' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'SO', tone: 'mint' },
        cells: {
          name: 'Sam Ortiz',
          role: 'Developer',
          utilization: { kind: 'pill', label: '42%', tone: 'sky' },
          project: { kind: 'muted', value: 'Available from Monday' },
          rate: { kind: 'money', value: '$150/hr' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'AC', tone: 'amber' },
        cells: {
          name: 'Alex Chen',
          role: 'Copywriter',
          utilization: { kind: 'pill', label: '64%', tone: 'amber' },
          project: 'Maple Street social',
          rate: { kind: 'money', value: '$110/hr' },
        },
      },
    ],
  },
}

const buildSuppliersLandingConfig = (
  terminology: SupplierLandingTerminology = DEFAULT_SUPPLIER_LANDING_TERMINOLOGY,
): ModuleLandingConfig => ({
  slug: 'suppliers',
  title: terminology.Plural,
  subtitle: `The printers, photographers, and freelancers you buy from — what they charge, how long they take, and what is on order.`,
  icon: TruckIcon,
  accent: 'teal',
  features: [
    {
      icon: BuildingOfficeIcon,
      title: `One record per ${terminology.singular}`,
      body: 'Contacts, payment terms, and the items they supply, each with its unit cost and lead time.',
    },
    {
      icon: PackageIcon,
      title: 'Purchase orders',
      body: 'Issue a PO, then receive it in parts as deliveries arrive. What is still owed stays visible.',
    },
    {
      icon: StarIcon,
      title: `Rate your ${terminology.plural}`,
      body: `Keep a rating on each ${terminology.singular} so the next project goes to the one who delivered.`,
    },
    {
      icon: FilesIcon,
      title: 'Paperwork attached',
      body: 'Quotes, invoices, and agreements live on the record instead of in someone’s inbox.',
    },
  ],
  preview: {
    label: terminology.plural,
    caption: `The ${terminology.plural} behind your projects, with open orders, typical lead time, and payment terms.`,
    columns: [
      { key: 'name', label: terminology.Singular },
      { key: 'category', label: 'Supplies' },
      { key: 'orders', label: 'Open POs', align: 'right' },
      { key: 'lead', label: 'Lead time' },
      { key: 'terms', label: 'Terms' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'BP', tone: 'slate' },
        cells: {
          name: 'Brightline Print Co.',
          category: 'Printing',
          orders: '3',
          lead: { kind: 'muted', value: '5 days' },
          terms: { kind: 'muted', value: 'Net 30' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'NP', tone: 'sky' },
        cells: {
          name: 'Northfield Photo Studio',
          category: 'Photography',
          orders: '1',
          lead: { kind: 'muted', value: '2 weeks' },
          terms: { kind: 'muted', value: 'Net 15' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'CL', tone: 'amber' },
        cells: {
          name: 'Copper Lane Stock',
          category: 'Stock images',
          orders: '0',
          lead: { kind: 'muted', value: 'Same day' },
          terms: { kind: 'muted', value: 'Due on receipt' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'KT', tone: 'rose' },
        cells: {
          name: 'Kestrel Translation',
          category: 'Translation',
          orders: '2',
          lead: { kind: 'muted', value: '4 days' },
          terms: { kind: 'muted', value: 'Net 30' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'SS', tone: 'lilac' },
        cells: {
          name: 'Signal Sign Shop',
          category: 'Signage',
          orders: '1',
          lead: { kind: 'muted', value: '3 weeks' },
          terms: { kind: 'muted', value: 'Net 45' },
        },
      },
    ],
  },
})

const suppliers = buildSuppliersLandingConfig()

const contracts: ModuleLandingConfig = {
  slug: 'contracts',
  title: 'Contracts',
  subtitle:
    'Master agreements, statements of work, and retainers — with their value, dates, and renewals in one place instead of a shared drive.',
  icon: FileTextIcon,
  accent: 'indigo',
  features: [
    {
      icon: ArrowsClockwiseIcon,
      title: 'Retainer plans',
      body: 'Monthly hours or fees with scheduled changes, credits, and overage handled the way you bill it.',
    },
    {
      icon: BellRingingIcon,
      title: 'Renewal reminders',
      body: 'A heads-up 60 and 30 days before a contract renews or ends, or whenever your team prefers.',
    },
    {
      icon: FilesIcon,
      title: 'Change orders',
      body: 'Record scope changes against the contract, so the signed number and the real number agree.',
    },
    {
      icon: SignatureIcon,
      title: 'Signatures on record',
      body: 'Track who signed and when, and keep the signed copy on the contract.',
    },
  ],
  preview: {
    label: 'contracts',
    caption:
      'Every agreement with your clients, with its value and the date it renews or ends.',
    columns: [
      { key: 'name', label: 'Contract' },
      { key: 'value', label: 'Value', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'renews', label: 'Renews or ends' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'HP', tone: 'mint' },
        cells: {
          name: 'Harbor & Pine — Master services agreement',
          value: { kind: 'money', value: '$96,000/yr' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          renews: { kind: 'muted', value: 'Renews Mar 12, 2027' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MS', tone: 'amber' },
        cells: {
          name: 'Maple Street — Social media retainer',
          value: { kind: 'money', value: '$4,500/mo' },
          status: { kind: 'pill', label: 'Active', tone: 'mint' },
          renews: { kind: 'muted', value: 'Renews monthly' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'BC', tone: 'sky' },
        cells: {
          name: 'Bluewater — Mobile app statement of work',
          value: { kind: 'money', value: '$124,000' },
          status: { kind: 'pill', label: 'Sent', tone: 'amber' },
          renews: { kind: 'muted', value: 'Ends Aug 30, 2027' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'FD', tone: 'lilac' },
        cells: {
          name: 'Fieldstone — Brand refresh',
          value: { kind: 'money', value: '$32,500' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
          renews: { kind: 'muted', value: 'Not started' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'LA', tone: 'rose' },
        cells: {
          name: 'Lumen Architects — Website care plan',
          value: { kind: 'money', value: '$900/mo' },
          status: { kind: 'pill', label: 'Expired', tone: 'rose' },
          renews: { kind: 'muted', value: 'Ended Sep 30' },
        },
      },
    ],
  },
}

const events: ModuleLandingConfig = {
  slug: 'events',
  title: 'Events',
  subtitle:
    'Workshops, client reviews, open houses, and offsites — with the agenda, the guest list, and every RSVP in one place.',
  icon: CalendarBlankIcon,
  accent: 'rose',
  features: [
    {
      icon: ListChecksIcon,
      title: 'Agendas and sessions',
      body: 'Plan one afternoon or several days, with sessions, tracks, speakers, and hosts.',
    },
    {
      icon: EnvelopeSimpleIcon,
      title: 'RSVPs and waitlists',
      body: 'Collect yes, no, and maybe, approve guests when you need to, and keep a waitlist once you are full.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Capacity limits',
      body: 'Cap the event or a single session, so the hands-on workshop never has more people than seats.',
    },
    {
      icon: TagIcon,
      title: 'Types and statuses',
      body: 'Workshops, reviews, trainings, and company events, each marked upcoming, on hold, or completed.',
    },
  ],
  preview: {
    label: 'events',
    caption:
      'What is coming up, what kind of event it is, and how many people have said yes.',
    columns: [
      { key: 'name', label: 'Event' },
      { key: 'type', label: 'Type' },
      { key: 'date', label: 'Date' },
      { key: 'rsvps', label: 'Going', align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'OH', tone: 'rose' },
        cells: {
          name: 'Spring open house',
          type: 'Company event',
          date: { kind: 'muted', value: 'May 14' },
          rsvps: '86',
          status: { kind: 'pill', label: 'Upcoming', tone: 'sky' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BW', tone: 'amber' },
        cells: {
          name: 'Brand workshop with Fieldstone',
          type: 'Workshop',
          date: { kind: 'muted', value: 'Nov 6' },
          rsvps: '8',
          status: { kind: 'pill', label: 'Upcoming', tone: 'sky' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'QR', tone: 'sky' },
        cells: {
          name: 'Quarterly review with Bluewater',
          type: 'Review',
          date: { kind: 'muted', value: 'Nov 20' },
          rsvps: '5',
          status: { kind: 'pill', label: 'Upcoming', tone: 'sky' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'NH', tone: 'mint' },
        cells: {
          name: 'New-hire onboarding',
          type: 'Training',
          date: { kind: 'muted', value: 'Oct 13' },
          rsvps: '3',
          status: { kind: 'pill', label: 'Completed', tone: 'mint' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'TO', tone: 'lilac' },
        cells: {
          name: 'Team offsite',
          type: 'Company event',
          date: { kind: 'muted', value: 'TBD' },
          rsvps: '—',
          status: { kind: 'pill', label: 'On hold', tone: 'slate' },
        },
      },
    ],
  },
}

const tickets: ModuleLandingConfig = {
  slug: 'tickets',
  title: 'Tickets',
  subtitle:
    'Client requests and support in one queue, next to the projects they belong to — with response times you can promise.',
  icon: TicketIcon,
  accent: 'fuchsia',
  features: [
    {
      icon: TimerIcon,
      title: 'Response targets',
      body: 'Set first-response and resolution times per ticket type, with a warning before one is missed.',
    },
    {
      icon: ChatsCircleIcon,
      title: 'Internal notes',
      body: 'Reply to the client and talk with your team on the same ticket. Internal notes never reach the client.',
    },
    {
      icon: CheckCircleIcon,
      title: 'Requests become tasks',
      body: 'Turn a request into a task on the right project without retyping it, one at a time or in bulk.',
    },
    {
      icon: ChartBarIcon,
      title: 'Insights',
      body: 'First-response time, resolution time, backlog, missed targets, and satisfaction, by type and agent.',
    },
  ],
  preview: {
    label: 'tickets · open',
    caption:
      'Requests from your clients, how urgent each one is, and whose move it is next.',
    columns: [
      { key: 'name', label: 'Ticket' },
      { key: 'client', label: 'Client' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status' },
      { key: 'owner', label: 'Assignee' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'LP', tone: 'rose' },
        cells: {
          name: 'Members can’t sign in',
          client: 'Bluewater Credit Union',
          priority: { kind: 'pill', label: 'Urgent', tone: 'rose' },
          status: { kind: 'pill', label: 'In progress', tone: 'amber' },
          owner: { kind: 'muted', value: 'Sam O.' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'HH', tone: 'amber' },
        cells: {
          name: 'Update holiday hours on the site',
          client: 'Harbor & Pine Coffee',
          priority: { kind: 'pill', label: 'High', tone: 'amber' },
          status: { kind: 'pill', label: 'Triaged', tone: 'sky' },
          owner: { kind: 'muted', value: 'Jordan E.' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'TP', tone: 'sky' },
        cells: {
          name: 'New team photos for About page',
          client: 'Fieldstone Dental',
          priority: { kind: 'pill', label: 'Medium', tone: 'slate' },
          status: {
            kind: 'pill',
            label: 'Waiting on requester',
            tone: 'lilac',
          },
          owner: { kind: 'muted', value: 'Maya K.' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'IQ', tone: 'mint' },
        cells: {
          name: 'Question about October invoice',
          client: 'Northline Outfitters',
          priority: { kind: 'pill', label: 'Medium', tone: 'slate' },
          status: { kind: 'pill', label: 'New', tone: 'sky' },
          owner: { kind: 'muted', value: 'Unassigned' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'MP', tone: 'lilac' },
        cells: {
          name: 'Swap the menu PDF',
          client: 'Maple Street Bakery',
          priority: { kind: 'pill', label: 'Low', tone: 'slate' },
          status: { kind: 'pill', label: 'Resolved', tone: 'mint' },
          owner: { kind: 'muted', value: 'Alex C.' },
        },
      },
    ],
  },
}

const collections: ModuleLandingConfig = {
  slug: 'collections',
  title: 'Collections',
  subtitle:
    'Your own lists, shaped like your work — brand assets, photo releases, a freelancer roster — without a spreadsheet on the side.',
  icon: StackIcon,
  accent: 'amber',
  features: [
    {
      icon: GearSixIcon,
      title: 'Fields you choose',
      body: 'Text, dates, choices, files, links to clients or projects, and formulas that add things up.',
    },
    {
      icon: ListChecksIcon,
      title: 'Saved views',
      body: 'Filter and sort once, then keep the view for yourself or share it with the team.',
    },
    {
      icon: LockSimpleIcon,
      title: 'Private, team, or shared',
      body: 'Decide who can see each collection, from just you to the whole workspace.',
    },
    {
      icon: BellRingingIcon,
      title: 'Date reminders',
      body: 'Get reminded before a date in an entry comes due, like a license renewal or a release expiring.',
    },
  ],
  preview: {
    label: 'collections',
    caption:
      'Lists your team keeps that do not fit a standard module, each with the fields it needs.',
    columns: [
      { key: 'name', label: 'Collection' },
      { key: 'entries', label: 'Entries', align: 'right' },
      { key: 'access', label: 'Access' },
      { key: 'updated', label: 'Updated' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'BA', tone: 'amber' },
        cells: {
          name: 'Brand assets',
          entries: '247',
          access: { kind: 'pill', label: 'Shared', tone: 'mint' },
          updated: { kind: 'muted', value: 'Today' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'PR', tone: 'sky' },
        cells: {
          name: 'Photo releases',
          entries: '211',
          access: { kind: 'pill', label: 'Team', tone: 'sky' },
          updated: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'FR', tone: 'rose' },
        cells: {
          name: 'Freelancer roster',
          entries: '34',
          access: { kind: 'pill', label: 'Team', tone: 'sky' },
          updated: { kind: 'muted', value: 'Oct 12' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'SL', tone: 'mint' },
        cells: {
          name: 'Software licenses',
          entries: '18',
          access: { kind: 'pill', label: 'Private', tone: 'slate' },
          updated: { kind: 'muted', value: 'Sep 30' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'AW', tone: 'lilac' },
        cells: {
          name: 'Awards and press',
          entries: '56',
          access: { kind: 'pill', label: 'Shared', tone: 'mint' },
          updated: { kind: 'muted', value: 'Aug 21' },
        },
      },
    ],
  },
}

const marketing: ModuleLandingConfig = {
  slug: 'marketing',
  title: 'Marketing',
  subtitle:
    'Email campaigns and follow-up journeys sent from the same place your clients and deals live — so you can see which emails turned into work.',
  icon: MegaphoneIcon,
  accent: 'rose',
  features: [
    {
      icon: EnvelopeSimpleIcon,
      title: 'Campaigns with A/B tests',
      body: 'Design an email from blocks, test two subject lines, and send the winner by opens or clicks.',
    },
    {
      icon: PathIcon,
      title: 'Journeys',
      body: 'Multi-step follow-ups that enroll people automatically and stop once they reach your goal.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Segments',
      body: 'Fixed lists, or audiences that update themselves, like “customers who haven’t booked in 90 days.”',
    },
    {
      icon: HandshakeIcon,
      title: 'Opens, clicks, and deals',
      body: 'Open and click rates per send, plus the deals each campaign led to and what they were worth.',
    },
  ],
  preview: {
    label: 'marketing · campaigns',
    caption:
      'Your own campaigns, who they went to, and how each one performed.',
    columns: [
      { key: 'name', label: 'Campaign' },
      { key: 'segment', label: 'Sent to' },
      { key: 'opens', label: 'Opened', align: 'right' },
      { key: 'clicks', label: 'Clicked', align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'FP', tone: 'rose' },
        cells: {
          name: 'Fall portfolio update',
          segment: 'Past clients · 412',
          opens: '48%',
          clicks: '7.2%',
          status: { kind: 'pill', label: 'Sent', tone: 'mint' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BB', tone: 'amber' },
        cells: {
          name: 'Brand basics webinar invite',
          segment: 'Prospects · 1,280',
          opens: { kind: 'muted', value: '—' },
          clicks: { kind: 'muted', value: '—' },
          status: { kind: 'pill', label: 'Scheduled', tone: 'sky' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'NL', tone: 'sky' },
        cells: {
          name: 'October newsletter',
          segment: 'Subscribers · 2,418',
          opens: '41%',
          clicks: '5.6%',
          status: { kind: 'pill', label: 'Sent', tone: 'mint' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'HO', tone: 'mint' },
        cells: {
          name: 'Holiday ordering reminder',
          segment: 'Retail clients · 38',
          opens: { kind: 'muted', value: '—' },
          clicks: { kind: 'muted', value: '—' },
          status: { kind: 'pill', label: 'Pending approval', tone: 'amber' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'WB', tone: 'lilac' },
        cells: {
          name: 'We miss you',
          segment: 'Lapsed clients · 64',
          opens: { kind: 'muted', value: '—' },
          clicks: { kind: 'muted', value: '—' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
        },
      },
    ],
  },
}

const invoices: ModuleLandingConfig = {
  slug: 'invoices',
  title: 'Invoices',
  subtitle:
    'Bill straight from the time and expenses your team already tracked, send it, and get paid online — without rebuilding it in another app.',
  icon: ReceiptIcon,
  accent: 'emerald',
  features: [
    {
      icon: TimerIcon,
      title: 'Built from tracked time',
      body: 'A billing run gathers each client’s approved hours and expenses into a draft you can review.',
    },
    {
      icon: CreditCardIcon,
      title: 'Online payments',
      body: 'Clients pay by card from the invoice link, and the payment is recorded for you.',
    },
    {
      icon: CheckCircleIcon,
      title: 'Approval before sending',
      body: 'Route invoices to a reviewer first, so nothing reaches a client with the wrong number on it.',
    },
    {
      icon: CalculatorIcon,
      title: 'Aging and QuickBooks',
      body: 'See what is overdue and by how long, and sync invoices to QuickBooks Online.',
    },
  ],
  preview: {
    label: 'invoices',
    caption:
      'What each client owes, when it is due, and what has already been paid.',
    columns: [
      { key: 'name', label: 'Invoice' },
      { key: 'client', label: 'Client' },
      { key: 'amount', label: 'Amount', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'due', label: 'Due' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'HP', tone: 'mint' },
        cells: {
          name: 'INV-1042',
          client: 'Harbor & Pine Coffee',
          amount: { kind: 'money', value: '$12,400.00' },
          status: { kind: 'pill', label: 'Paid', tone: 'mint' },
          due: { kind: 'muted', value: 'Oct 15' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BC', tone: 'sky' },
        cells: {
          name: 'INV-1043',
          client: 'Bluewater Credit Union',
          amount: { kind: 'money', value: '$31,000.00' },
          status: { kind: 'pill', label: 'Sent', tone: 'sky' },
          due: { kind: 'muted', value: 'Nov 15' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'FD', tone: 'lilac' },
        cells: {
          name: 'INV-1044',
          client: 'Fieldstone Dental',
          amount: { kind: 'money', value: '$8,125.00' },
          status: { kind: 'pill', label: 'Partially paid', tone: 'amber' },
          due: { kind: 'muted', value: 'Nov 1' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'NO', tone: 'rose' },
        cells: {
          name: 'INV-1039',
          client: 'Northline Outfitters',
          amount: { kind: 'money', value: '$6,750.00' },
          status: { kind: 'pill', label: 'Overdue', tone: 'rose' },
          due: { kind: 'muted', value: 'Sep 30' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'MS', tone: 'amber' },
        cells: {
          name: 'INV-1045',
          client: 'Maple Street Bakery',
          amount: { kind: 'money', value: '$4,500.00' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
          due: { kind: 'muted', value: 'Nov 30' },
        },
      },
    ],
  },
}

const quotes: ModuleLandingConfig = {
  slug: 'quotes',
  title: 'Quotes',
  subtitle:
    'Price the work from your own price book, send a link, and know the moment the client says yes.',
  icon: FileTextIcon,
  accent: 'sky',
  features: [
    {
      icon: LinkSimpleIcon,
      title: 'Accept online',
      body: 'Clients open the quote from a link and accept or decline it there. You see when they viewed it.',
    },
    {
      icon: TagIcon,
      title: 'From your price book',
      body: 'Add services and products with their saved prices and tax, then adjust quantities per quote.',
    },
    {
      icon: HandshakeIcon,
      title: 'Tied to the deal',
      body: 'Quotes live on their deal, and an accepted quote shows right on the deal record.',
    },
    {
      icon: CalendarBlankIcon,
      title: 'Expiry dates',
      body: 'Set how long a price holds. Quotes past their date expire on their own.',
    },
  ],
  preview: {
    label: 'quotes',
    caption:
      'Proposals out with clients, what they are worth, and where each one stands.',
    columns: [
      { key: 'name', label: 'Quote' },
      { key: 'client', label: 'Client' },
      { key: 'amount', label: 'Total', align: 'right' },
      { key: 'status', label: 'Status' },
      { key: 'expires', label: 'Valid until' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'WR', tone: 'mint' },
        cells: {
          name: 'Website redesign',
          client: 'Harbor & Pine Coffee',
          amount: { kind: 'money', value: '$48,000.00' },
          status: { kind: 'pill', label: 'Viewed', tone: 'lilac' },
          expires: { kind: 'muted', value: 'Nov 21' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'BR', tone: 'sky' },
        cells: {
          name: 'Brand refresh',
          client: 'Fieldstone Dental',
          amount: { kind: 'money', value: '$32,500.00' },
          status: { kind: 'pill', label: 'Accepted', tone: 'mint' },
          expires: { kind: 'muted', value: 'Nov 8' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'SC', tone: 'amber' },
        cells: {
          name: 'Spring catalog shoot',
          client: 'Northline Outfitters',
          amount: { kind: 'money', value: '$18,000.00' },
          status: { kind: 'pill', label: 'Sent', tone: 'sky' },
          expires: { kind: 'muted', value: 'Dec 1' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'SR', tone: 'rose' },
        cells: {
          name: 'Signage package',
          client: 'Lumen Architects',
          amount: { kind: 'money', value: '$9,800.00' },
          status: { kind: 'pill', label: 'Declined', tone: 'rose' },
          expires: { kind: 'muted', value: 'Oct 10' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'MB', tone: 'lilac' },
        cells: {
          name: 'Mobile banking launch',
          client: 'Bluewater Credit Union',
          amount: { kind: 'money', value: '$124,000.00' },
          status: { kind: 'pill', label: 'Draft', tone: 'slate' },
          expires: { kind: 'muted', value: '—' },
        },
      },
    ],
  },
}

const products: ModuleLandingConfig = {
  slug: 'products',
  title: 'Products',
  subtitle:
    'One price book for everything you sell — services, retainers, and goods — used by every quote and invoice.',
  icon: TagIcon,
  accent: 'teal',
  features: [
    {
      icon: TagIcon,
      title: 'Saved prices',
      body: 'Name, SKU, and unit price once, then add the item to a quote or invoice in a click.',
    },
    {
      icon: CalculatorIcon,
      title: 'Tax and income accounts',
      body: 'Mark items taxable, set the rate, and choose the income account they post to.',
    },
    {
      icon: ArrowsClockwiseIcon,
      title: 'Synced with QuickBooks',
      body: 'Keep your product list matched with QuickBooks Online instead of keeping two lists.',
    },
    {
      icon: LinkSimpleIcon,
      title: 'See where it’s used',
      body: 'Before you change a price, see which quotes and invoices include the item.',
    },
  ],
  preview: {
    label: 'products',
    caption:
      'The services and goods you sell, with the price every quote starts from.',
    columns: [
      { key: 'name', label: 'Product' },
      { key: 'sku', label: 'SKU' },
      { key: 'type', label: 'Type' },
      { key: 'price', label: 'Unit price', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'BW', tone: 'mint' },
        cells: {
          name: 'Brand strategy workshop',
          sku: { kind: 'muted', value: 'BRD-WKSP' },
          type: 'Service',
          price: { kind: 'money', value: '$3,500.00' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'WP', tone: 'sky' },
        cells: {
          name: 'Website page design',
          sku: { kind: 'muted', value: 'WEB-PAGE' },
          type: 'Service',
          price: { kind: 'money', value: '$850.00' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'SM', tone: 'amber' },
        cells: {
          name: 'Social media management',
          sku: { kind: 'muted', value: 'SOC-MONTH' },
          type: 'Service',
          price: { kind: 'money', value: '$1,800.00' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'PD', tone: 'rose' },
        cells: {
          name: 'Photography day rate',
          sku: { kind: 'muted', value: 'PHO-DAY' },
          type: 'Service',
          price: { kind: 'money', value: '$2,200.00' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'BC', tone: 'lilac' },
        cells: {
          name: 'Printed business cards (500)',
          sku: { kind: 'muted', value: 'PRT-CARD' },
          type: 'Goods',
          price: { kind: 'money', value: '$95.00' },
        },
      },
    ],
  },
}

const profitability: ModuleLandingConfig = {
  slug: 'profitability',
  title: 'Profitability',
  subtitle:
    'Which clients and projects actually make money, once every hour and expense is counted — and what the next quarter looks like.',
  icon: ChartPieSliceIcon,
  accent: 'indigo',
  features: [
    {
      icon: ChartPieSliceIcon,
      title: 'Margin by project',
      body: 'Revenue, cost, gross profit, and margin for every project and client, from time already tracked.',
    },
    {
      icon: TrendUpIcon,
      title: 'Budget burn',
      body: 'See how much of each budget is spent before the project runs over, not after.',
    },
    {
      icon: ChartLineUpIcon,
      title: 'Revenue forecast',
      body: 'A range for the months ahead, plus the renewals at risk and whether the team can deliver it.',
    },
    {
      icon: CheckCircleIcon,
      title: 'Billing approvals',
      body: 'Review what is ready to bill each month and approve it in one place.',
    },
  ],
  preview: {
    label: 'profitability · this quarter',
    caption:
      'This quarter’s projects ranked by what they earned after the cost of the team’s time.',
    columns: [
      { key: 'name', label: 'Project' },
      { key: 'client', label: 'Client' },
      { key: 'revenue', label: 'Revenue', align: 'right' },
      { key: 'cost', label: 'Cost', align: 'right' },
      { key: 'margin', label: 'Margin', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'SR', tone: 'mint' },
        cells: {
          name: 'Social media retainer',
          client: 'Maple Street Bakery',
          revenue: { kind: 'money', value: '$13,500' },
          cost: { kind: 'money', value: '$6,210' },
          margin: { kind: 'pill', label: '54%', tone: 'mint' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'WR', tone: 'sky' },
        cells: {
          name: 'Website redesign',
          client: 'Harbor & Pine Coffee',
          revenue: { kind: 'money', value: '$36,000' },
          cost: { kind: 'money', value: '$19,800' },
          margin: { kind: 'pill', label: '45%', tone: 'mint' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'MB', tone: 'lilac' },
        cells: {
          name: 'Mobile banking launch',
          client: 'Bluewater Credit Union',
          revenue: { kind: 'money', value: '$62,000' },
          cost: { kind: 'money', value: '$41,540' },
          margin: { kind: 'pill', label: '33%', tone: 'amber' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'BR', tone: 'amber' },
        cells: {
          name: 'Brand refresh',
          client: 'Fieldstone Dental',
          revenue: { kind: 'money', value: '$16,250' },
          cost: { kind: 'money', value: '$13,650' },
          margin: { kind: 'pill', label: '16%', tone: 'rose' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'SC', tone: 'rose' },
        cells: {
          name: 'Spring catalog shoot',
          client: 'Northline Outfitters',
          revenue: { kind: 'money', value: '$9,000' },
          cost: { kind: 'money', value: '$10,350' },
          margin: { kind: 'pill', label: '−15%', tone: 'rose' },
        },
      },
    ],
  },
}

const knowledgeBase: ModuleLandingConfig = {
  slug: 'knowledge_base',
  title: 'Knowledge base',
  subtitle:
    'How your team does things, written down once — for new hires, for clients, and for Benny when someone asks.',
  icon: BookOpenTextIcon,
  accent: 'violet',
  features: [
    {
      icon: BookOpenTextIcon,
      title: 'Articles and categories',
      body: 'Write processes, checklists, and how-tos, and keep drafts private until they are ready.',
    },
    {
      icon: PaperPlaneTiltIcon,
      title: 'Share with clients',
      body: 'Publish an article to your public help site or to a client’s portal, and keep the rest internal.',
    },
    {
      icon: SparkleIcon,
      title: 'Benny knows it',
      body: 'Benny reads your published articles, so “how do we hand off brand files?” gets your answer.',
    },
    {
      icon: FilePdfIcon,
      title: 'Views and PDF export',
      body: 'See which articles people actually read, and export any article as a PDF.',
    },
  ],
  preview: {
    label: 'knowledge base',
    caption:
      'The articles your team relies on, who can see each one, and how often it is read.',
    columns: [
      { key: 'name', label: 'Article' },
      { key: 'category', label: 'Category' },
      { key: 'audience', label: 'Visible to' },
      { key: 'views', label: 'Views', align: 'right' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'BH', tone: 'lilac' },
        cells: {
          name: 'Brand file handoff checklist',
          category: 'Delivery',
          audience: { kind: 'pill', label: 'Team', tone: 'sky' },
          views: '318',
        },
      },
      {
        id: '2',
        avatar: { initials: 'RC', tone: 'mint' },
        cells: {
          name: 'How to request a design change',
          category: 'For clients',
          audience: { kind: 'pill', label: 'Client portal', tone: 'mint' },
          views: '1,204',
        },
      },
      {
        id: '3',
        avatar: { initials: 'NH', tone: 'amber' },
        cells: {
          name: 'New-hire first week',
          category: 'People',
          audience: { kind: 'pill', label: 'Team', tone: 'sky' },
          views: '96',
        },
      },
      {
        id: '4',
        avatar: { initials: 'MR', tone: 'sky' },
        cells: {
          name: 'Monthly client report',
          category: 'Delivery',
          audience: { kind: 'pill', label: 'Team', tone: 'sky' },
          views: '142',
        },
      },
      {
        id: '5',
        avatar: { initials: 'BF', tone: 'rose' },
        cells: {
          name: 'Billing and payment FAQ',
          category: 'For clients',
          audience: { kind: 'pill', label: 'Help site', tone: 'lilac' },
          views: '2,880',
        },
      },
    ],
  },
}

const liveChat: ModuleLandingConfig = {
  slug: 'live_chat',
  title: 'Live chat',
  subtitle:
    'Talk with people while they’re on your website, and keep every conversation with the contacts and tickets it turns into.',
  icon: ChatsCircleIcon,
  accent: 'sky',
  features: [
    {
      icon: GlobeSimpleIcon,
      title: 'On your website',
      body: 'A chat tab on your site’s widget that follows your hours and timezone, with an offline message for after hours.',
    },
    {
      icon: LightningIcon,
      title: 'Replies while you’re away',
      body: 'Automatic replies answer from your own notes — hours, services, links to share — when no one is online.',
    },
    {
      icon: UserCircleIcon,
      title: 'Visitors become contacts',
      body: 'Anyone who leaves an email is added to Contacts, or matched to the contact you already have.',
    },
    {
      icon: TicketIcon,
      title: 'Chats become tickets',
      body: 'When a conversation needs follow-up, turn it into a ticket in one click, transcript included.',
    },
  ],
  preview: {
    label: 'live chat · inbox',
    caption:
      'People chatting on your website, and what each conversation turned into.',
    columns: [
      { key: 'name', label: 'Visitor' },
      { key: 'topic', label: 'Asked about' },
      { key: 'status', label: 'Status' },
      { key: 'outcome', label: 'Outcome' },
      { key: 'started', label: 'Started' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'DW', tone: 'sky' },
        cells: {
          name: 'Dana Whitfield',
          topic: 'Website refresh for Fieldstone Dental',
          status: { kind: 'pill', label: 'Open', tone: 'mint' },
          outcome: { kind: 'muted', value: 'Contact added' },
          started: { kind: 'muted', value: '2 min ago' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MR', tone: 'amber' },
        cells: {
          name: 'Marco Ruiz',
          topic: 'Spring ad launch dates',
          status: { kind: 'pill', label: 'Open', tone: 'mint' },
          outcome: { kind: 'muted', value: 'Ticket created' },
          started: { kind: 'muted', value: '14 min ago' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'PS', tone: 'rose' },
        cells: {
          name: 'Priya Shah',
          topic: 'Menu photo shoot pricing',
          status: { kind: 'pill', label: 'Closed', tone: 'slate' },
          outcome: { kind: 'muted', value: 'Contact added' },
          started: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'TB', tone: 'lilac' },
        cells: {
          name: 'Tom Becker',
          topic: 'Monthly social media plan',
          status: { kind: 'pill', label: 'Closed', tone: 'slate' },
          outcome: { kind: 'muted', value: 'Ticket created' },
          started: { kind: 'muted', value: 'Oct 14' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'JL', tone: 'mint' },
        cells: {
          name: 'Jordan Lee',
          topic: 'Office hours on Saturdays',
          status: { kind: 'pill', label: 'Closed', tone: 'slate' },
          outcome: { kind: 'muted', value: 'Answered while away' },
          started: { kind: 'muted', value: 'Oct 12' },
        },
      },
    ],
  },
}

const scheduling: ModuleLandingConfig = {
  slug: 'scheduling',
  title: 'Scheduling',
  subtitle:
    'Booking links your clients use to pick a time with your team — only when you’re free, with the calendar invite and video link handled.',
  icon: CalendarCheckIcon,
  accent: 'emerald',
  features: [
    {
      icon: CalendarBlankIcon,
      title: 'Booking links',
      body: 'Set the length, buffers, minimum notice, and how far ahead people can book, from your team’s weekly hours.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Only when you’re free',
      body: 'Times are checked against connected calendars and Scrambl events, and round robin spreads meetings across hosts.',
    },
    {
      icon: VideoCameraIcon,
      title: 'Meeting links included',
      body: 'Each booking lands on the host’s calendar with a Google Meet, Microsoft Teams, or Zoom link.',
    },
    {
      icon: UserCircleIcon,
      title: 'Invitees become contacts',
      body: 'New invitees are added to Contacts, and can cancel or reschedule from their confirmation email.',
    },
  ],
  preview: {
    label: 'scheduling · bookings',
    caption:
      'Clients who booked time with you, and the meeting each one picked.',
    columns: [
      { key: 'name', label: 'Invitee' },
      { key: 'client', label: 'Client' },
      { key: 'meeting', label: 'Booked' },
      { key: 'when', label: 'When' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'RK', tone: 'sky' },
        cells: {
          name: 'Rachel Kim',
          client: 'Fieldstone Dental',
          meeting: 'Website kickoff',
          when: { kind: 'muted', value: 'Today, 2:00 PM' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MB', tone: 'amber' },
        cells: {
          name: 'Marcus Bell',
          client: 'Harbor & Pine Coffee',
          meeting: 'Monthly check-in',
          when: { kind: 'muted', value: 'Tomorrow, 9:30 AM' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'EO', tone: 'lilac' },
        cells: {
          name: 'Elena Ortiz',
          client: 'Bluewater Credit Union',
          meeting: 'Brand discovery call',
          when: { kind: 'muted', value: 'Thu, 11:00 AM' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'SP', tone: 'rose' },
        cells: {
          name: 'Sam Patel',
          client: 'Maple Street Bakery',
          meeting: 'Photo shoot walkthrough',
          when: { kind: 'muted', value: 'Fri, 3:00 PM' },
          status: { kind: 'pill', label: 'Canceled', tone: 'rose' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'NL', tone: 'mint' },
        cells: {
          name: 'Nora Lind',
          client: 'Lumen Architects',
          meeting: 'Brand discovery call',
          when: { kind: 'muted', value: 'Mon, 10:00 AM' },
          status: { kind: 'pill', label: 'Confirmed', tone: 'mint' },
        },
      },
    ],
  },
}

const analytics: ModuleLandingConfig = {
  slug: 'analytics',
  title: 'Analytics',
  subtitle:
    'Dashboards, saved reports, and alerts across your clients, projects, and team time — so the numbers you check every Monday are already waiting.',
  icon: ChartLineUpIcon,
  accent: 'indigo',
  features: [
    {
      icon: ChartBarIcon,
      title: 'Dashboards',
      body: 'Metric cards, charts, funnels, gauges, and trends on one board, private to you or shared with the team.',
    },
    {
      icon: ListChecksIcon,
      title: 'Saved reports',
      body: 'Time, utilization, project, financial, and pipeline reports with the columns, filters, and totals you choose.',
    },
    {
      icon: PaperPlaneTiltIcon,
      title: 'Reports on a schedule',
      body: 'Email a report every day, week, or month as a PDF, CSV, or Excel file, or send it right now.',
    },
    {
      icon: BellRingingIcon,
      title: 'Alerts',
      body: 'Rules that flag overdue invoices, projects at risk of missing a deadline, slipping client health, and overbooked people.',
    },
  ],
  preview: {
    label: 'analytics · alerts',
    caption:
      'What your alert rules caught across clients, projects, and the team, and where each one stands.',
    columns: [
      { key: 'name', label: 'Alert' },
      { key: 'client', label: 'Client' },
      { key: 'type', label: 'Type' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'OI', tone: 'rose' },
        cells: {
          name: 'Invoice INV-1042 is 18 days overdue',
          client: 'Northline Outfitters',
          type: { kind: 'muted', value: 'Overdue invoice' },
          priority: { kind: 'pill', label: 'High', tone: 'amber' },
          status: { kind: 'pill', label: 'Active', tone: 'sky' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'DR', tone: 'amber' },
        cells: {
          name: 'Website redesign is at risk of missing its deadline',
          client: 'Fieldstone Dental',
          type: { kind: 'muted', value: 'Deadline risk' },
          priority: { kind: 'pill', label: 'Critical', tone: 'rose' },
          status: { kind: 'pill', label: 'Acknowledged', tone: 'lilac' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'CH', tone: 'lilac' },
        cells: {
          name: 'Relationship health dropped to 48',
          client: 'Bluewater Credit Union',
          type: { kind: 'muted', value: 'Client health risk' },
          priority: { kind: 'pill', label: 'High', tone: 'amber' },
          status: { kind: 'pill', label: 'Active', tone: 'sky' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'HU', tone: 'sky' },
        cells: {
          name: 'Maya K. is booked at 118% this week',
          client: { kind: 'muted', value: 'Your team' },
          type: { kind: 'muted', value: 'High utilization' },
          priority: { kind: 'pill', label: 'Medium', tone: 'slate' },
          status: { kind: 'pill', label: 'Snoozed', tone: 'slate' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'OI', tone: 'mint' },
        cells: {
          name: 'Invoice INV-1036 is 9 days overdue',
          client: 'Maple Street Bakery',
          type: { kind: 'muted', value: 'Overdue invoice' },
          priority: { kind: 'pill', label: 'Low', tone: 'slate' },
          status: { kind: 'pill', label: 'Resolved', tone: 'mint' },
        },
      },
    ],
  },
}

const communications: ModuleLandingConfig = {
  slug: 'calling',
  title: 'Communications',
  subtitle:
    'Every call, email, and meeting with your clients in one log — who you talked to, how it went, and what happens next.',
  icon: PhoneIcon,
  accent: 'orange',
  features: [
    {
      icon: PhoneIcon,
      title: 'Log calls in seconds',
      body: 'Record the direction, outcome, talk time, and notes, linked to the contact and deal it was about.',
    },
    {
      icon: CheckCircleIcon,
      title: 'Follow-ups that stick',
      body: 'Create a follow-up task as you log the call, so the callback lands on someone’s list.',
    },
    {
      icon: EnvelopeSimpleIcon,
      title: 'Emails and meetings too',
      body: 'Emails synced from Gmail or Outlook and recorded meetings show up in the same log.',
    },
    {
      icon: ChartBarIcon,
      title: 'Your week in numbers',
      body: 'Calls this week, connect rate, meetings booked, and talk time, filtered by type and outcome.',
    },
  ],
  preview: {
    label: 'communications · log',
    caption:
      'Your team’s calls and conversations with clients, and how each one went.',
    columns: [
      { key: 'name', label: 'Contact' },
      { key: 'client', label: 'Client' },
      { key: 'type', label: 'Type' },
      { key: 'outcome', label: 'Outcome' },
      { key: 'when', label: 'When' },
    ],
    rows: [
      {
        id: '1',
        avatar: { initials: 'GH', tone: 'sky' },
        cells: {
          name: 'Grace Holloway',
          client: 'Lumen Architects',
          type: 'Call · 12:40',
          outcome: { kind: 'pill', label: 'Meeting booked', tone: 'sky' },
          when: { kind: 'muted', value: 'Today, 10:15 AM' },
        },
      },
      {
        id: '2',
        avatar: { initials: 'MB', tone: 'amber' },
        cells: {
          name: 'Marcus Bell',
          client: 'Harbor & Pine Coffee',
          type: 'Call · 4:05',
          outcome: { kind: 'pill', label: 'Connected', tone: 'mint' },
          when: { kind: 'muted', value: 'Today, 9:30 AM' },
        },
      },
      {
        id: '3',
        avatar: { initials: 'EO', tone: 'lilac' },
        cells: {
          name: 'Elena Ortiz',
          client: 'Bluewater Credit Union',
          type: 'Meeting',
          outcome: { kind: 'muted', value: 'Q4 campaign review' },
          when: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '4',
        avatar: { initials: 'TB', tone: 'mint' },
        cells: {
          name: 'Tom Becker',
          client: 'Northline Outfitters',
          type: 'Call · 0:45',
          outcome: { kind: 'pill', label: 'Voicemail', tone: 'slate' },
          when: { kind: 'muted', value: 'Yesterday' },
        },
      },
      {
        id: '5',
        avatar: { initials: 'SP', tone: 'rose' },
        cells: {
          name: 'Sam Patel',
          client: 'Maple Street Bakery',
          type: 'Call · 2:10',
          outcome: {
            kind: 'pill',
            label: 'Callback requested',
            tone: 'lilac',
          },
          when: { kind: 'muted', value: 'Oct 16' },
        },
      },
    ],
  },
}

/**
 * Default catch-all — no preview table, generic copy.
 * Used when SubscriptionFeatureGuard receives a feature key we don't have
 * a tailored landing for.
 */
export const DEFAULT_LANDING_CONFIG: ModuleLandingConfig = {
  slug: 'default',
  eyebrow: 'Pro feature',
  title: 'This feature is part of Pro',
  subtitle:
    'You’ve found a corner of Scrambl that’s reserved for Pro plans. Upgrade once, unlock all of it — including the modules in your sidebar marked Pro.',
  icon: SparkleIcon,
  accent: 'amber',
  features: [
    {
      icon: LightningIcon,
      title: 'Every module unlocked',
      body: 'Forms, Contacts, Deals, Team, Suppliers, Contracts, Events, and more — all included.',
    },
    {
      icon: UsersThreeIcon,
      title: 'Bigger teams, real workflows',
      body: 'Up to 50 seats, unlimited projects, custom fields, and templates. No surprise gates.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Pro-grade controls',
      body: 'Workflows, integrations, data export, watermark removal, and form CAPTCHA.',
    },
    {
      icon: SparkleIcon,
      // Benny is a teammate, never "AI"; keep the two apart in one card.
      title: 'More from Benny',
      body: '4× the monthly credits, plus first access to new things Benny can do.',
    },
  ],
}

/**
 * What the generic landing calls a gated page that has no tailored landing,
 * in the words its settings nav uses. Clients is left out: organizations
 * rename it, and the generic copy reads fine without a name.
 */
const DEFAULT_LANDING_FEATURE_NAMES: Partial<Record<string, string>> = {
  audit_log: 'Audit trail',
  custom_permission_sets: 'Custom permission sets',
  integrations: 'Integrations',
  quickbooks_online: 'QuickBooks Online',
  mcp_server: 'AI assistants',
  online_payments: 'Online payments',
  // Gated modules without a tailored landing, by their route titles.
  sequences: 'Sequences',
  enrichment: 'Data quality',
}

export const MODULE_LANDING_CONFIGS: Record<string, ModuleLandingConfig> = {
  forms,
  contacts,
  deals,
  team,
  suppliers,
  contracts,
  events,
  tickets,
  collections,
  marketing,
  invoices,
  quotes,
  products,
  profitability,
  knowledge_base: knowledgeBase,
  live_chat: liveChat,
  scheduling,
  analytics,
  calling: communications,
}

export function getModuleLandingConfig(
  featureKey: string,
  options: ModuleLandingConfigOptions = {},
): ModuleLandingConfig {
  if (featureKey === 'suppliers') {
    return buildSuppliersLandingConfig(options.supplierTerminology)
  }

  const moduleConfig = MODULE_LANDING_CONFIGS[featureKey]
  if (moduleConfig) return moduleConfig

  // No tailored landing: the generic one still names what was locked.
  const name = DEFAULT_LANDING_FEATURE_NAMES[featureKey]
  return name
    ? { ...DEFAULT_LANDING_CONFIG, name, title: `${name} is part of Pro` }
    : DEFAULT_LANDING_CONFIG
}
